import { Link } from 'react-router-dom';
import { Form } from 'react-bootstrap';

import SalesReportPager from './SalesReportPager';
import {
    PRODUCT_SORT_OPTIONS,
    activeProductSort,
    companyLabel,
    formatCount,
    formatMoney,
} from '../../../utils/salesReportUtils';

const SortHeader = ({ label, field, activeField, sortOrder, onSort, numeric = false }) => {
    const active = activeField === field;
    return (
        <th className={numeric ? 'sr-num' : undefined} aria-sort={active ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}>
            <button type="button" className="sr-sort-btn" onClick={() => onSort(field)}>
                {label}
                {active ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : ''}
            </button>
        </th>
    );
};

const SalesReportProductsTable = ({
    products = [],
    totals,
    total = 0,
    page = 1,
    pages = 0,
    pageSize = 20,
    productSortBy,
    sortOrder = 'desc',
    isUpdating = false,
    onPage,
    onPageSize,
    onSort,
    onShowOrders,
}) => {
    const activeField = activeProductSort({ productSortBy });

    return (
        <section className={`sr-table-card${isUpdating ? ' sr-updating' : ''}`} aria-busy={isUpdating}>
            <div className="sr-section-head">
                <div>
                    <h2>Productos</h2>
                    <p className="sr-hint mb-0">
                        Cada fila es precio por cantidad, menos el descuento aplicado a la línea. El IVA y el envío se quedan en el reporte del pedido.
                    </p>
                </div>
                <div className="sr-table-toolbar">
                    <Form.Group className="sr-field mb-0">
                        <Form.Label column={false}>Ordenar por</Form.Label>
                        <Form.Select
                            aria-label="Ordenar productos"
                            value={activeField}
                            onChange={(event) => onSort(event.target.value)}
                        >
                            {PRODUCT_SORT_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </div>
            </div>

            {totals && (
                <div className="sr-secondary">
                    <article className="sr-stat">
                        <p className="sr-kicker">Neto de líneas</p>
                        <strong>{formatMoney(totals.lineNet)}</strong>
                    </article>
                    <article className="sr-stat">
                        <p className="sr-kicker">Bruto de líneas</p>
                        <strong>{formatMoney(totals.lineGross)}</strong>
                    </article>
                    <article className="sr-stat">
                        <p className="sr-kicker">Descuento</p>
                        <strong>{formatMoney(totals.lineDiscount)}</strong>
                    </article>
                    <article className="sr-stat">
                        <p className="sr-kicker">Unidades</p>
                        <strong>{formatCount(totals.unitsSold)}</strong>
                    </article>
                </div>
            )}

            {!products.length ? (
                <p className="sr-empty mb-0">Ninguna línea coincide con estos filtros.</p>
            ) : (
                <div className="sr-table-wrap">
                    <table className="sr-table">
                        <thead>
                            <tr>
                                <SortHeader label="Producto" field="name" activeField={activeField} sortOrder={sortOrder} onSort={onSort} />
                                <th>Empresa</th>
                                <SortHeader label="Unidades" field="unitsSold" numeric activeField={activeField} sortOrder={sortOrder} onSort={onSort} />
                                <SortHeader label="Pedidos" field="orderCount" numeric activeField={activeField} sortOrder={sortOrder} onSort={onSort} />
                                <SortHeader label="Bruto" field="lineGross" numeric activeField={activeField} sortOrder={sortOrder} onSort={onSort} />
                                <SortHeader label="Descuento" field="lineDiscount" numeric activeField={activeField} sortOrder={sortOrder} onSort={onSort} />
                                <SortHeader label="Neto" field="lineNet" numeric activeField={activeField} sortOrder={sortOrder} onSort={onSort} />
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {products.map((product, index) => {
                                const key = [
                                    product.productId,
                                    product.contificoCode,
                                    product.company?.ruc,
                                    product.variation,
                                    index,
                                ].join('-');
                                return (
                                    <tr key={key}>
                                        <td data-label="Producto">
                                            <span>
                                                {product.productId ? (
                                                    <Link to={`/product/${product.productId}`}>{product.name || 'Producto'}</Link>
                                                ) : (
                                                    product.name || 'Producto'
                                                )}
                                                {product.variation && (
                                                    <span className="sr-subline">
                                                        {product.variationType ? `${product.variationType}: ` : ''}
                                                        {product.variation}
                                                    </span>
                                                )}
                                                {product.contificoCode && (
                                                    <span className="sr-subline">Contífico {product.contificoCode}</span>
                                                )}
                                            </span>
                                        </td>
                                        <td data-label="Empresa">{companyLabel(product.company)}</td>
                                        <td data-label="Unidades" className="sr-num">
                                            {formatCount(product.unitsSold)}
                                        </td>
                                        <td data-label="Pedidos" className="sr-num">
                                            {formatCount(product.orderCount)}
                                        </td>
                                        <td data-label="Bruto" className="sr-num">
                                            {formatMoney(product.lineGross)}
                                        </td>
                                        <td data-label="Descuento" className="sr-num">
                                            {formatMoney(product.lineDiscount)}
                                        </td>
                                        <td data-label="Neto" className="sr-num">
                                            {formatMoney(product.lineNet)}
                                        </td>
                                        <td data-label="Acciones">
                                            {product.productId && (
                                                <button
                                                    type="button"
                                                    className="sr-btn sr-btn-ghost sr-btn-small"
                                                    onClick={() => onShowOrders(product.productId)}
                                                >
                                                    Ver pedidos
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <SalesReportPager
                page={page}
                pages={pages}
                total={total}
                pageSize={pageSize}
                onPage={onPage}
                onPageSize={onPageSize}
            />
        </section>
    );
};

export default SalesReportProductsTable;
